import { format, formatDistance } from 'date-fns'

// Currency formatter for Indian Rupees
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

// Short currency format (₹1.2L, ₹50K)
export function formatCurrencyShort(amount: number): string {
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(1)}Cr`
  }
  if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(1)}L`
  }
  if (amount >= 1000) {
    return `₹${(amount / 1000).toFixed(1)}K`
  }
  return `₹${amount.toFixed(0)}`
}

// Date formatters
export function formatDate(date: string | Date): string {
  return format(new Date(date), 'dd/MM/yyyy')
}

export function formatDateTime(date: string | Date): string {
  return format(new Date(date), 'dd/MM/yyyy HH:mm')
}

export function formatDateLong(date: string | Date): string {
  return format(new Date(date), 'dd MMMM yyyy')
}

export function formatRelative(date: string | Date): string {
  return formatDistance(new Date(date), new Date(), { addSuffix: true })
}

// Phone formatter for Indian numbers
export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 10) {
    return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`
  }
  return phone
}

// GSTIN formatter
export function formatGSTIN(gstin: string): string {
  if (gstin.length === 15) {
    return `${gstin.slice(0, 2)} ${gstin.slice(2, 12)} ${gstin.slice(12)}`
  }
  return gstin
}

// Job card number display
export function formatJobCard(jobCard: string): string {
  return jobCard.toUpperCase()
}

// Turnaround time in days: 0 -> "< 1 day", otherwise whole number
export function formatTurnaround(days: number): string {
  const rounded = Math.round(days ?? 0)
  return rounded === 0 ? '< 1 day' : `${rounded} day${rounded === 1 ? '' : 's'}`
}

// Status badge color mapping
export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    // Work order statuses
    received: 'badge-info',
    in_progress: 'badge-warning',
    completed: 'badge-success',
    dispatched: 'badge-gray',

    // Invoice statuses
    generated: 'badge-info',
    partially_paid: 'badge-warning',
    paid: 'badge-success',
    overdue: 'badge-error',
    cancelled: 'badge-gray',

    // Ticket statuses
    open: 'badge-info',
    resolved: 'badge-success',
    closed: 'badge-gray',

    // User roles
    admin: 'badge-error',
    manager: 'badge-warning',
    technician: 'badge-info',
    billing: 'badge-success',

    // Default
    default: 'badge-gray',
  }

  return colors[status.toLowerCase()] || colors.default
}

// Status label display
export function formatStatus(status: string): string {
  const labels: Record<string, string> = {
    received: 'Received',
    in_progress: 'In Progress',
    completed: 'Completed',
    dispatched: 'Dispatched',
    generated: 'Generated',
    partially_paid: 'Partially Paid',
    paid: 'Paid',
    overdue: 'Overdue',
    cancelled: 'Cancelled',
    open: 'Open',
    resolved: 'Resolved',
    closed: 'Closed',
  }

  return labels[status.toLowerCase()] || status
}

// Number to words (Indian system)
export function numberToWords(num: number): string {
  if (num === 0) return 'Zero'

  const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen']
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']

  const convertLessThanThousand = (n: number): string => {
    if (n === 0) return ''
    if (n < 20) return units[n]
    if (n < 100) return `${tens[Math.floor(n / 10)]} ${units[n % 10]}`.trim()
    return `${units[Math.floor(n / 100)]} Hundred ${convertLessThanThousand(n % 100)}`.trim()
  }

  const rupees = Math.floor(num)
  const paise = Math.round((num - rupees) * 100)

  let result = ''

  if (rupees >= 10000000) {
    result += `${convertLessThanThousand(Math.floor(rupees / 10000000))} Crore `
  }
  if (rupees >= 100000) {
    result += `${convertLessThanThousand(Math.floor((rupees % 10000000) / 100000))} Lakh `
  }
  if (rupees >= 1000) {
    result += `${convertLessThanThousand(Math.floor((rupees % 100000) / 1000))} Thousand `
  }
  if (rupees % 1000 > 0) {
    result += convertLessThanThousand(rupees % 1000)
  }

  result = result.trim() + ' Rupees'

  if (paise > 0) {
    result += ` and ${convertLessThanThousand(paise)} Paise`
  }

  return result + ' Only'
}
